import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { addMinutes, todayISO } from "@/lib/utils-app";

// V1.38.3 · Contra-propuesta de horario del profesor al alumno.
// V1.38.4 · También sirve para "Modificar" una cita ya aceptada
// (`mode="edit"`): en ese modo hace PATCH directo en lugar de
// counter-propose y no envía WhatsApp automático.
// Reutiliza el patrón "Inicio | [Min/clase ⇧ Clases] | Fin" del modal
// de creación de citas para que el profesor no calcule minutos a mano.
export default function CounterProposeDialog({ open, appointment, mode = "counter", onClose, onSaved, onSendWhatsapp }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({ date: todayISO(), time: "10:00", end_time: "10:45" });
  const [minutesPerClass, setMinutesPerClass] = useState(45);
  const [classesCount, setClassesCount] = useState(1);
  const [saving, setSaving] = useState(false);
  const [notifyWhatsapp, setNotifyWhatsapp] = useState(!isEdit);

  useEffect(() => {
    if (!open || !appointment) return;
    // En modo edición partimos de la fecha/hora actual de la cita.
    // En modo contra-propuesta partimos de la propuesta del alumno.
    const seedDate = isEdit
      ? (appointment.date || todayISO())
      : (appointment.requested_date || appointment.date || todayISO());
    const seedTime = isEdit
      ? (appointment.time || "10:00")
      : (appointment.requested_time || appointment.time || "10:00");
    const seedEnd = isEdit
      ? (appointment.end_time || addMinutes(seedTime, 45))
      : (appointment.requested_end_time || appointment.end_time || addMinutes(seedTime, 45));
    setForm({ date: seedDate, time: seedTime, end_time: seedEnd });
    const [sh, sm] = seedTime.split(":").map(Number);
    const [eh, em] = seedEnd.split(":").map(Number);
    const total = (eh * 60 + em) - (sh * 60 + sm);
    if (total > 0 && total % 45 === 0) {
      setMinutesPerClass(45);
      setClassesCount(Math.max(1, total / 45));
    } else if (total > 0) {
      setMinutesPerClass(total);
      setClassesCount(1);
    } else {
      setMinutesPerClass(45);
      setClassesCount(1);
    }
    setNotifyWhatsapp(!isEdit);
  }, [open, appointment, isEdit]);

  const handleTimeChange = (t) => {
    setForm((f) => ({ ...f, time: t, end_time: addMinutes(t, classesCount * minutesPerClass) }));
  };
  const handleClassesChange = (raw) => {
    const n = Math.max(1, parseInt(raw || "0", 10) || 1);
    setClassesCount(n);
    setForm((f) => ({ ...f, end_time: addMinutes(f.time, n * minutesPerClass) }));
  };
  const handleMinutesChange = (raw) => {
    const n = Math.max(1, parseInt(raw || "0", 10) || 1);
    setMinutesPerClass(n);
    setForm((f) => ({ ...f, end_time: addMinutes(f.time, classesCount * n) }));
  };
  const handleEndChange = (v) => setForm((f) => ({ ...f, end_time: v }));

  const submit = async () => {
    if (!form.date || !form.time) {
      toast.error("Fecha y hora son obligatorias");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        // PATCH directo — la cita se guarda con el nuevo horario, sin
        // pasar por el flujo de contra-propuesta (no altera requested_*).
        await api.patch(`/appointments/${appointment.id}`, {
          date: form.date, time: form.time, end_time: form.end_time,
        });
        toast.success("Cita modificada");
      } else {
        await api.post(`/appointments/${appointment.id}/counter-propose`, form);
        toast.success("Contra-propuesta guardada");
      }
      if (notifyWhatsapp && onSendWhatsapp) {
        // Envía por WhatsApp automáticamente con el mensaje default
        onSendWhatsapp({ ...appointment, ...form });
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="counter-propose-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modificar cita" : "Contra-proponer nueva fecha"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!isEdit ? (
            <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900">
              <div className="font-semibold mb-0.5">Propuesta actual del alumno:</div>
              <div className="font-mono">
                {appointment?.requested_date || "?"} · {appointment?.requested_time || "?"}
                {appointment?.requested_end_time && `–${appointment.requested_end_time}`}
              </div>
            </div>
          ) : (
            <div className="rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2 text-xs text-neutral-700">
              <div className="font-semibold mb-0.5">Horario actual:</div>
              <div className="font-mono">
                {appointment?.date || "?"} · {appointment?.time || "?"}
                {appointment?.end_time && `–${appointment.end_time}`}
              </div>
            </div>
          )}

          <div className="grid grid-cols-[1fr,1fr,110px,1fr] gap-3">
            <div>
              <label className="text-sm font-semibold text-neutral-700 mb-1.5 block">Fecha *</label>
              <input
                data-testid="counter-date"
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full rounded-xl border border-neutral-300 bg-white px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-neutral-700 mb-1.5 block">Inicio *</label>
              <input
                data-testid="counter-time"
                type="time"
                value={form.time}
                onChange={(e) => handleTimeChange(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 bg-white px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex flex-col gap-1">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-0.5 block">
                  Min/clase
                </label>
                <input
                  data-testid="counter-minutes-per-class"
                  type="number" min="1" step="1"
                  value={minutesPerClass}
                  onChange={(e) => handleMinutesChange(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 bg-white px-2 h-[22px] text-xs font-mono tabular-nums text-center focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-orange-600 mb-0.5 block">
                  Clases
                </label>
                <input
                  data-testid="counter-classes"
                  type="number" min="1" step="1"
                  value={classesCount}
                  onChange={(e) => handleClassesChange(e.target.value)}
                  className="w-full rounded-lg border border-orange-300 bg-orange-50 px-2 h-[22px] text-xs font-mono tabular-nums text-center font-bold text-orange-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-neutral-700 mb-1.5 block">Fin *</label>
              <input
                data-testid="counter-end-time"
                type="time"
                value={form.end_time}
                onChange={(e) => handleEndChange(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 bg-white px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={notifyWhatsapp}
                   onChange={(e) => setNotifyWhatsapp(e.target.checked)}
                   data-testid="counter-notify-wa" />
            <span>
              {isEdit
                ? "Avisar al alumno del cambio por WhatsApp"
                : "Enviar contra-propuesta al alumno por WhatsApp"}
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-secondary !h-9 !px-3 !text-xs">Cancelar</button>
          <button onClick={submit} disabled={saving} className="btn-primary !h-9 !px-4 !text-xs" data-testid="counter-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {isEdit ? "Guardar cambios" : "Enviar contra-propuesta"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
